import { Injectable } from '@angular/core';
import {
  UnparsedUserAppConfiguration,
  UserAppConfiguration,
} from '@models/user-app-config.class';
import { AppManagementService } from '@services/appManagement.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable()
/**
 * Class for User entity services
 */
export class UserService {
  /**
   * Works as a temporary helper that contains the last known config
   * (either fetched via GET, or patched within the App State).
   *
   * It's used to keep track of the last properties, so we can override
   * only some of them when patching it.
   */
  public lastKnownConfig: UnparsedUserAppConfiguration;
  public currentUserAppConfiguration$ =
    new BehaviorSubject<UserAppConfiguration>(null);

  constructor(private appManagementRestService: AppManagementService) {}

  /**
   * Get user app configuration service that calls REST API
   *
   * @returns -
   */
  public getUserAppConfiguration(): Observable<UserAppConfiguration> {
    return this.appManagementRestService.getUserAppConfiguration().pipe(
      /**
       * FIXME: This should be a map instead of a tap, and it should
       * work to have all the UserAppConfiguration ready to use, without
       * needing to parse/stringify everytime we need to read/save
       */
      tap((userAppConfiguration: UnparsedUserAppConfiguration) => {
        this.lastKnownConfig = {
          ...userAppConfiguration,
          uiConfiguration: userAppConfiguration.uiConfiguration || '{}',
        };

        this.currentUserAppConfiguration$.next(
          this.convertToParsedConfiguration(this.lastKnownConfig),
        );
      }),
    ) as Observable<UserAppConfiguration>;
  }

  /**
   * Update user app configuration. Previous config will get wiped.
   *
   * @param config containing a complete User configuration
   * @returns a request observable to subscribe to
   */
  public updateUserAppConfiguration(
    config: UnparsedUserAppConfiguration,
  ): Observable<any> {
    return this.appManagementRestService
      .updateUserAppConfiguration(config)
      .pipe(
        tap(() => {
          this.currentUserAppConfiguration$.next(
            this.convertToParsedConfiguration(config),
          );
        }),
      );
  }

  /**
   * Overrides the value for some properties inside the last UserAppConfiguration
   *
   * @param patchedProperties that will be appended to the existing config object
   * @returns a request observable to subscribe to
   */
  public patchUserConfiguration(
    patchedProperties: Partial<UnparsedUserAppConfiguration>,
  ): Observable<any> {
    const patchedConfig = { ...this.lastKnownConfig, ...patchedProperties };
    this.lastKnownConfig = patchedConfig;
    return this.updateUserAppConfiguration(patchedConfig);
  }

  /**
   * Update user app configuration service that calls REST API
   *
   * @param string contains the user's pass
   * @param string contains the verify code
   * @returns -
   */
  public activateUser(
    userCode: string,
    token: string,
    userName: string,
  ): Observable<any> {
    return this.appManagementRestService.activateUser({
      userCode,
      token,
      userName,
    });
  }

  private convertToParsedConfiguration(
    config: UnparsedUserAppConfiguration,
  ): UserAppConfiguration {
    const parsedUserAppConfiguration: UserAppConfiguration = {
      ...config,
    } as UserAppConfiguration;

    if (
      config.hasOwnProperty('uiConfiguration') &&
      typeof config.uiConfiguration === 'string'
    ) {
      parsedUserAppConfiguration.uiConfiguration = JSON.parse(
        config.uiConfiguration,
      );
    }
    return parsedUserAppConfiguration;
  }
}
